import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Tag, Button, Space, Spin, Alert, Drawer, Descriptions, Popconfirm } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminUsers,
  getAdminUser,
  suspendAdminUser,
  reactivateAdminUser,
  getProviderFeeTermsList,
  IAdminUserSummary,
  IAdminUserBooking,
  IAdminUserDetail,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import ProviderFeeTermsModal from '../../../components/Admin/ProviderFeeTermsModal';
import { useAuth } from '../../../contextProviders/AuthProvider';
import { IProviderFeeTerms } from '../../../interfaces/heavyDutyMachinery/IProviderFeeTerms';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, dateSorter, enumColumnFilters } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;
const { Search } = Input;

const ROLE_OPTIONS = ['Admin', 'Moderator', 'User'];

// HeavyDutyMachineryUser table filterable by role and suspended/active status,
// suspend (with a reason, HeavyDutyMachineryUser.IsSuspended/SuspendedAt/
// SuspendedReason) / reactivate, and a per-user detail with recent
// bookings. Suspending is enforced at Login/RefreshToken (see
// AuthController), not just cosmetic here.
const UsersSection: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [users, setUsers] = useState<IAdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [isSuspended, setIsSuspended] = useState<boolean | undefined>(undefined);
  const [search, setSearch] = useState<string | undefined>(undefined);

  // Who is on something other than the standard commission, keyed by user.
  // One call for the whole list rather than one per row: the table needs the
  // badge, and the modal needs the numbers, and both come from here.
  // Admin-only, matching AdminProviderFeeTermsController's own guard -- a
  // moderator never sees any of this.
  const { isAdmin } = useAuth();
  const [feeTermsByUser, setFeeTermsByUser] = useState<Record<string, IProviderFeeTerms>>({});
  const [platformDefaults, setPlatformDefaults] = useState({ percent: 0, fixed: 0 });
  const [feeTermsTarget, setFeeTermsTarget] = useState<IAdminUserSummary | undefined>(undefined);

  const [detail, setDetail] = useState<IAdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [suspendTargetId, setSuspendTargetId] = useState<string | undefined>(undefined);

  const loadUsers = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminUsers(role, isSuspended, search)
      .then(setUsers)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [role, isSuspended, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!isAdmin) return;
    getProviderFeeTermsList()
      .then((list) => {
        setPlatformDefaults({ percent: list.defaultPercent, fixed: list.defaultFixed });
        setFeeTermsByUser(Object.fromEntries(list.terms.map((terms) => [terms.providerUserId, terms])));
      })
      // Deliberately silent: the commission badge is extra information on a
      // screen whose job is listing users. Failing to load it should not put
      // an error over a list that loaded fine.
      .catch(() => undefined);
  }, [isAdmin]);

  const handleFeeTermsSaved = (userId: string, terms: IProviderFeeTerms | null) => {
    setFeeTermsByUser((prev) => {
      const next = { ...prev };
      if (terms) next[userId] = terms;
      else delete next[userId];
      return next;
    });
  };

  /** "1,5% + 0,20 €", or just "0%" when there is no fixed half to mention. */
  const formatTerms = (terms: IProviderFeeTerms) =>
    terms.fixedAmount > 0
      ? `${terms.percentOfAmount}% + ${terms.fixedAmount} ${terms.currencyCode}`
      : `${terms.percentOfAmount}%`;

  const openDetail = (id: string) => {
    setDetailLoading(true);
    setDetail({} as IAdminUserDetail);
    getAdminUser(id)
      .then(setDetail)
      .catch(() => {
        message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const handleSuspend = async (reason: string) => {
    if (!suspendTargetId) return;
    try {
      await suspendAdminUser(suspendTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setSuspendTargetId(undefined);
      loadUsers();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateAdminUser(id);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      loadUsers();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  // Shared by the desktop "Acciones" column and the mobile card footer.
  const renderActions = (record: IAdminUserSummary) => (
    <Space wrap>
      <Button size="small" onClick={() => openDetail(record.userId)}>
        {translate(t, TRANSLATIONS.ADMIN_USERS_VIEW_BUTTON)}
      </Button>
      {isAdmin && (
        <Button size="small" onClick={() => setFeeTermsTarget(record)}>
          {translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_BUTTON)}
        </Button>
      )}
      {record.isSuspended ? (
        <Popconfirm title={translate(t, TRANSLATIONS.ADMIN_USERS_REACTIVATE_CONFIRM)} onConfirm={() => handleReactivate(record.userId)}>
          <Button size="small">{translate(t, TRANSLATIONS.ADMIN_USERS_REACTIVATE_BUTTON)}</Button>
        </Popconfirm>
      ) : (
        <Button size="small" danger onClick={() => setSuspendTargetId(record.userId)}>
          {translate(t, TRANSLATIONS.ADMIN_USERS_SUSPEND_BUTTON)}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_USERS)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_USERS_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_USERS_ROLE_FILTER_ALL)}
          style={{ maxWidth: 160, width: '100%' }}
          value={role}
          onChange={setRole}
          options={ROLE_OPTIONS.map((value) => ({ value, label: value }))}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_USERS_STATUS_FILTER_ALL)}
          style={{ maxWidth: 160, width: '100%' }}
          value={isSuspended === undefined ? undefined : String(isSuspended)}
          onChange={(value) => setIsSuspended(value === undefined ? undefined : value === 'true')}
          options={[
            { value: 'false', label: translate(t, TRANSLATIONS.ADMIN_USERS_STATUS_ACTIVE) },
            { value: 'true', label: translate(t, TRANSLATIONS.ADMIN_USERS_STATUS_SUSPENDED) },
          ]}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_USERS_LOAD_ERROR)} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={<IconUsers size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_USERS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_USERS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminUserSummary>
          rowKey="userId"
          dataSource={users}
          pagination={{ pageSize: 20 }}
          toCard={(record) => ({
            key: record.userId,
            title: record.displayName,
            subtitle: record.email,
            badge: (
              <Tag color={record.isSuspended ? 'red' : 'green'} style={{ marginInlineEnd: 0 }}>
                {translate(t, record.isSuspended ? TRANSLATIONS.ADMIN_USERS_STATUS_SUSPENDED : TRANSLATIONS.ADMIN_USERS_STATUS_ACTIVE)}
              </Tag>
            ),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_USERS_COL_ROLE), value: record.role },
              { label: translate(t, TRANSLATIONS.ADMIN_USERS_COL_CREATED), value: new Date(record.createdAt).toLocaleDateString() },
              // Only rendered when there is something to say: a user on the
              // standard rate has no row, and AdminResponsiveList drops a
              // field whose value is undefined.
              {
                label: translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_COLUMN),
                value: feeTermsByUser[record.userId] ? formatTerms(feeTermsByUser[record.userId]) : undefined,
                wide: true,
              },
            ],
            actions: renderActions(record),
          })}
          // No stacked-cards redesign needed here (internal admin tool) --
          // just lets the table scroll horizontally within its own
          // container on a narrow screen instead of squeezing 6 columns
          // illegibly or breaking the page's own layout.
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_USERS_COL_NAME),
              dataIndex: 'displayName',
              key: 'displayName',
              sorter: textSorter<IAdminUserSummary>('displayName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_USERS_COL_EMAIL),
              dataIndex: 'email',
              key: 'email',
              sorter: textSorter<IAdminUserSummary>('email'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_USERS_COL_ROLE),
              dataIndex: 'role',
              key: 'role',
              filters: enumColumnFilters(ROLE_OPTIONS, (value) => value),
              onFilter: (value, record: IAdminUserSummary) => record.role === value,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_USERS_COL_STATUS),
              dataIndex: 'isSuspended',
              key: 'isSuspended',
              render: (value: boolean) => (
                <Tag color={value ? 'red' : 'green'}>
                  {translate(t, value ? TRANSLATIONS.ADMIN_USERS_STATUS_SUSPENDED : TRANSLATIONS.ADMIN_USERS_STATUS_ACTIVE)}
                </Tag>
              ),
            },
            // Admin-only, like the action that edits it: a moderator has no
            // business seeing what the platform charges anybody.
            ...(isAdmin
              ? [
                  {
                    title: translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_COLUMN),
                    key: 'feeTerms',
                    render: (_: unknown, record: IAdminUserSummary) =>
                      feeTermsByUser[record.userId] ? (
                        <Tag color="gold">{formatTerms(feeTermsByUser[record.userId])}</Tag>
                      ) : (
                        <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_FEE_TERMS_STANDARD)}</Text>
                      ),
                  },
                ]
              : []),
            {
              title: translate(t, TRANSLATIONS.ADMIN_USERS_COL_CREATED),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminUserSummary>('createdAt'),
              render: (value: string) => new Date(value).toLocaleDateString(),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_USERS_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminUserSummary) => renderActions(record),
            },
          ]}
        />
      )}

      <Drawer
        title={translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_TITLE)}
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={isMobile ? '100%' : 480}
      >
        {detailLoading || !detail?.userId ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_EMAIL_LABEL)}>{detail.email}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_PHONE_LABEL)}>{detail.phone || '—'}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_COUNTRY_LABEL)}>{detail.country || '—'}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_ROLE_LABEL)}>{detail.role}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_USERS_COL_STATUS)}>
                <Tag color={detail.isSuspended ? 'red' : 'green'}>
                  {translate(t, detail.isSuspended ? TRANSLATIONS.ADMIN_USERS_STATUS_SUSPENDED : TRANSLATIONS.ADMIN_USERS_STATUS_ACTIVE)}
                </Tag>
              </Descriptions.Item>
              {detail.isSuspended && detail.suspendedReason && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_SUSPENDED_REASON_LABEL)}>
                  {detail.suspendedReason}
                </Descriptions.Item>
              )}
              {detail.isSuspended && detail.suspendedAt && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_SUSPENDED_AT_LABEL)}>
                  {new Date(detail.suspendedAt).toLocaleDateString()}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Title level={5} style={{ marginBottom: 12 }}>
              {translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_BOOKINGS_TITLE)}
            </Title>
            {detail.recentBookings.length === 0 ? (
              <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_USERS_DETAIL_BOOKINGS_EMPTY)}</Text>
            ) : (
              <AdminResponsiveList<IAdminUserBooking>
                rowKey="id"
                size="small"
                dataSource={detail.recentBookings}
                pagination={false}
                scroll={{ x: 'max-content' }}
                toCard={(record) => ({
                  key: record.id,
                  title: record.heavyDutyMachineryTitle,
                  badge: <Tag style={{ marginInlineEnd: 0 }}>{record.status}</Tag>,
                  fields: [
                    {
                      label: translate(t, TRANSLATIONS.ADMIN_USERS_BOOKING_COL_DATES),
                      value: `${new Date(record.startAt).toLocaleDateString()} - ${new Date(record.endAt).toLocaleDateString()}`,
                    },
                    { label: translate(t, TRANSLATIONS.ADMIN_USERS_BOOKING_COL_PRICE), value: `${record.totalPrice} €` },
                  ],
                })}
                columns={[
                  {
                    title: translate(t, TRANSLATIONS.ADMIN_USERS_BOOKING_COL_SPACE),
                    dataIndex: 'heavyDutyMachineryTitle',
                    key: 'heavyDutyMachineryTitle',
                  },
                  {
                    title: translate(t, TRANSLATIONS.ADMIN_USERS_BOOKING_COL_DATES),
                    key: 'dates',
                    render: (_: unknown, record: IAdminUserBooking) =>
                      `${new Date(record.startAt).toLocaleDateString()} - ${new Date(record.endAt).toLocaleDateString()}`,
                  },
                  {
                    title: translate(t, TRANSLATIONS.ADMIN_USERS_BOOKING_COL_STATUS),
                    dataIndex: 'status',
                    key: 'status',
                  },
                  {
                    title: translate(t, TRANSLATIONS.ADMIN_USERS_BOOKING_COL_PRICE),
                    dataIndex: 'totalPrice',
                    key: 'totalPrice',
                    render: (value: number) => `${value} €`,
                  },
                ]}
              />
            )}
          </>
        )}
      </Drawer>

      <AdminReasonModal
        open={suspendTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_USERS_SUSPEND_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_USERS_SUSPEND_BUTTON)}
        onCancel={() => setSuspendTargetId(undefined)}
        onConfirm={handleSuspend}
      />

      <ProviderFeeTermsModal
        open={feeTermsTarget !== undefined}
        userId={feeTermsTarget?.userId}
        userLabel={feeTermsTarget ? `${feeTermsTarget.displayName} · ${feeTermsTarget.email}` : undefined}
        defaultPercent={platformDefaults.percent}
        defaultFixed={platformDefaults.fixed}
        onClose={() => setFeeTermsTarget(undefined)}
        onSaved={handleFeeTermsSaved}
      />
    </div>
  );
};

export default UsersSection;
